/**
 * Combo Select Component.
 *
 * A general-purpose combobox that combines the best of a <select> and
 * a search input. When closed it displays the selected value like a
 * normal select. When opened it shows all options in a scrollable
 * dropdown with a built-in filter input at the top for typeahead.
 *
 * The dropdown renders with `position: fixed` so it escapes parent
 * stacking contexts (cards with animations, overflow-hidden, etc.)
 * and always floats above all other content.
 *
 * Supports:
 * - Click to open → see all options immediately
 * - Type to filter options (typeahead)
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Optional "creatable" mode: offer to add new values inline
 * - Custom sublabels and icons per option
 * - Clearable selection
 */

import {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { Search, ChevronDown, Check, Plus, X } from "lucide-react";

export interface ComboOption {
  value: string;
  label: string;
  sublabel?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface ComboSelectProps {
  /** Available options. */
  options: ComboOption[];
  /** Currently selected value. */
  value: string;
  /** Called when the user selects an option. */
  onChange: (value: string) => void;
  /** Placeholder shown when nothing is selected. */
  placeholder?: string;
  /** Label text above the input. */
  label?: string;
  /** Whether the field is required. */
  required?: boolean;
  /** Whether the component is disabled. */
  disabled?: boolean;
  /** Allow clearing the selection. */
  clearable?: boolean;
  /** Allow creating new values by typing. */
  creatable?: boolean;
  /** Called when the user creates a new value (creatable mode). */
  onCreate?: (value: string) => void | Promise<void>;
  /** Additional class name. */
  className?: string;
}

function ComboSelect({
  options,
  value,
  onChange,
  placeholder = "Select...",
  label,
  required = false,
  disabled = false,
  clearable = false,
  creatable = false,
  onCreate,
  className,
}: ComboSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const filterRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase().trim();
    return options.filter(
      (o) =>
        (o.label && o.label.toLowerCase().includes(q)) ||
        (o.sublabel && o.sublabel.toLowerCase().includes(q)) ||
        (o.value && o.value.toLowerCase().includes(q)),
    );
  }, [options, query]);

  const queryHasExactMatch = useMemo(() => {
    if (!query.trim()) return true;
    const q = query.toLowerCase().trim();
    return options.some(
      (o) =>
        (o.label && o.label.toLowerCase() === q) ||
        (o.value && o.value.toLowerCase() === q),
    );
  }, [options, query]);

  useEffect(() => setHighlightIdx(0), [filtered.length]);

  // Calculate dropdown position from the trigger button.
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [isOpen]);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        // Also check if click is inside the portal dropdown.
        const dropdown = document.getElementById("combo-select-dropdown");
        if (dropdown && dropdown.contains(e.target as Node)) return;
        setIsOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  // Close on scroll/resize to avoid stale position.
  useEffect(() => {
    if (!isOpen) return;
    const close = () => {
      setIsOpen(false);
      setQuery("");
    };
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, [isOpen]);

  const handleSelect = useCallback(
    (optValue: string) => {
      onChange(optValue);
      setQuery("");
      setIsOpen(false);
    },
    [onChange],
  );

  const handleCreate = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setQuery("");
    setIsOpen(false);
    if (onCreate) {
      await onCreate(trimmed);
    } else {
      onChange(trimmed);
    }
  }, [query, onCreate, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen) {
        if (filtered.length > 0) {
          const item = filtered[highlightIdx];
          if (item && !item.disabled) handleSelect(item.value);
        } else if (creatable && query.trim() && !queryHasExactMatch) {
          handleCreate();
        }
      } else {
        setIsOpen(true);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setQuery("");
    } else if (e.key === "Tab") {
      setIsOpen(false);
      setQuery("");
    }
  };

  const openDropdown = () => {
    if (disabled) return;
    setIsOpen(true);
    setTimeout(() => filterRef.current?.focus(), 0);
  };

  // Render the dropdown in a portal so it escapes stacking contexts.
  const dropdown = isOpen
    ? createPortal(
        <div
          id="combo-select-dropdown"
          className="fixed z-[9999] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--surface-base)] shadow-[var(--shadow-lg)] animate-scale-in"
          style={{
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* Filter input */}
          <div className="relative border-b border-[var(--color-border)]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-tertiary)] pointer-events-none" />
            <input
              ref={filterRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                setTimeout(() => {
                  setIsOpen(false);
                  setQuery("");
                }, 200);
              }}
              onFocus={() => {
                // Cancel any pending blur close.
              }}
              placeholder="Type to filter..."
              className="w-full h-[36px] pl-9 pr-3 bg-transparent text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none"
            />
          </div>

          {/* Options list */}
          <div className="max-h-56 overflow-y-auto">
            {filtered.map((option, idx) => (
              <div
                key={option.value}
                onClick={() => {
                  if (!option.disabled) handleSelect(option.value);
                }}
                onMouseEnter={() => setHighlightIdx(idx)}
                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-[var(--color-border)]/30 last:border-0 transition-colors ${
                  option.disabled
                    ? "opacity-40 cursor-not-allowed"
                    : idx === highlightIdx
                      ? "bg-[var(--color-interactive-primary)]/[0.06]"
                      : "hover:bg-[var(--surface-sunken)]"
                }`}
              >
                {option.icon && <div className="shrink-0">{option.icon}</div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--text-primary)] truncate">
                    {option.label}
                  </p>
                  {option.sublabel && (
                    <p className="text-[11px] text-[var(--text-tertiary)] truncate">
                      {option.sublabel}
                    </p>
                  )}
                </div>
                {option.value === value && (
                  <Check className="h-4 w-4 text-[var(--color-interactive-primary)] shrink-0" />
                )}
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-[var(--text-tertiary)]">
                {query ? `No results for "${query}"` : "No options available"}
              </div>
            )}
          </div>

          {/* Create option */}
          {creatable && query.trim() && !queryHasExactMatch && (
            <div className="border-t border-[var(--color-border)]">
              <button
                type="button"
                onClick={handleCreate}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-medium text-brand-600 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/20 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add &ldquo;{query.trim()}&rdquo;
              </button>
            </div>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={className} ref={containerRef}>
      {label && (
        <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
          {label}
          {required && (
            <span className="ml-0.5 text-[var(--text-danger)]">*</span>
          )}
        </label>
      )}

      <div className="relative">
        {/* Trigger button — looks like a standard select */}
        <button
          ref={triggerRef}
          type="button"
          onClick={openDropdown}
          disabled={disabled}
          className="w-full h-[38px] flex items-center gap-2 pl-3 pr-10 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] text-left transition-all duration-200 hover:border-[var(--border-strong)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--border-focus)] disabled:opacity-50 disabled:pointer-events-none"
        >
          {selectedOption ? (
            <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
              {selectedOption.icon && (
                <div className="shrink-0">{selectedOption.icon}</div>
              )}
              <span className="text-sm text-[var(--text-primary)] truncate">
                {selectedOption.label}
              </span>
              {selectedOption.sublabel && (
                <span className="text-[10px] text-[var(--text-tertiary)] truncate shrink-0">
                  {selectedOption.sublabel}
                </span>
              )}
            </div>
          ) : value ? (
            <span className="text-sm text-[var(--text-primary)] truncate">
              {value}
            </span>
          ) : (
            <span className="text-sm text-[var(--text-tertiary)]">
              {placeholder}
            </span>
          )}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {clearable && value && !disabled && (
              <span
                role="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                }}
                className="p-0.5 rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown
              className={`h-3.5 w-3.5 text-[var(--text-tertiary)] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </div>
        </button>

        {dropdown}
      </div>
    </div>
  );
}

export default ComboSelect;
